<template>
  <div>
    <div class="container profile-form mb-5">
      <h2 class="form-title">Profile</h2>

      <div v-if="company" class="edit-customer-data base-form table-shadow">
        <div class="form-group mb-4">
          <div class="label-title">Company Info</div>
        </div>

        <VeeForm @submit="submit">
          <b-form-group class="form-group">
            <div class="form-inline-row">
              <label class="label-input">Email address</label>
              <VInput
                v-model="companyData.email"
                :rules="{ required: true, email: true }"
                autocomplete="off"
                type="email"
                name="email"
                placeholder="Email address"
              />
            </div>

            <div class="form-inline-row">
              <label class="label-input">Contact First name</label>
              <VInput
                v-model="companyData.contactName"
                :rules="{ required: true }"
                type="text"
                name="contactName"
                placeholder="First name"
              />
            </div>

            <div class="form-inline-row">
              <label class="label-input">Contact Last name</label>
              <VInput
                v-model="companyData.contactLastName"
                :rules="{ required: true }"
                type="text"
                name="contactLastName"
                placeholder="Last name"
              />
            </div>

            <div class="form-inline-row">
              <label class="label-input">Company</label>
              <VInput
                v-model="companyData.name"
                :rules="{ required: true, min: 3 }"
                type="text"
                name="name"
                placeholder="Company"
              />
            </div>

            <div class="form-inline-row">
              <label class="label-input">Phone number</label>
              <VInput
                v-model="companyData.phoneNumber"
                :rules="{ required: true }"
                type="text"
                name="phoneNumber"
                placeholder="Phone number"
              />
            </div>

            <div class="form-inline-row">
              <label class="label-input">External ID</label>
              <VInput
                v-model="companyData.externalId"
                :rules="{ required: false }"
                type="text"
                name="externalId"
                placeholder="External ID"
              />
            </div>

            <div class="raw text-right">
              <b-button variant="success" size="md" class="btn-green" type="submit"> Save </b-button>
            </div>

            <div class="form-group mb-4">
              <div class="label-title">Settings</div>
            </div>

            <div class="form-inline-row">
              <label class="label-input">Integrations</label>
              <b-form-group class="form-group position-relative">
                <b-button class="p-2 fz-inherit" variant="outline-secondary" @click="openIntegrationsModal">
                  <div class="d-flex align-items-center">
                    <mdicon name="CogOutline" class="color-black mr-2" size="24" />
                    <span class="color-black">Settings</span>
                  </div>
                </b-button>
              </b-form-group>
            </div>

            <div class="form-inline-row">
              <label class="label-input">Sender Addresses</label>
              <b-form-group class="form-group position-relative">
                <b-button class="p-2 fz-inherit" variant="outline-secondary" @click="openSenderAddressesModal">
                  <div class="d-flex align-items-center">
                    <mdicon name="CogOutline" class="color-black mr-2" size="24" />
                    <span class="color-black no-wrap">Settings</span>
                  </div>
                </b-button>
              </b-form-group>
            </div>

            <div class="form-inline-row">
              <label class="label-input">Users</label>
              <b-form-group class="form-group position-relative">
                <b-button class="p-2 fz-inherit" variant="outline-secondary" @click="openUsersModal">
                  <div class="d-flex align-items-center">
                    <mdicon name="CogOutline" class="color-black mr-2" size="24" />
                    <span class="color-black">Settings</span>
                  </div>
                </b-button>
              </b-form-group>
            </div>
          </b-form-group>
        </VeeForm>
      </div>
      <div v-else>
        <div class="text-center">
          <b-spinner class="mt-5" />
          <p class="mt-3">Loading company data...</p>
        </div>
      </div>
    </div>
    <SenderAddressesModal :id="SENDER_ADDRESSES" />
    <SenderAddressActionModal :id="SENDER_ADDRESS_ACTION" />

    <UsersModal :id="USERS" />
    <UserCreateEditModal :id="USERS_CREATE_EDIT" />

    <IntegrationsActionModal :id="INTEGRATIONS" />
    <IntegrationsDetailsModal :id="INTEGRATION_DETAILS" />
    <IntegrationsOrdersModal :id="INTEGRATION_ORDERS" />
    <IntegrationsMagentoKeysModal :id="MAGENTO_KEYS" />
    <IntegrationsWoocommerceKeysModal :id="WOO_COMMERCE_KEYS" />
    <IntegrationsShopifySettingsModal :id="SHOPIFY_WEBHOOK_SETTINGS" />
    <IntegrationsOngoingSettingsModal :id="ONGOING_WEBHOOK_SETTINGS" />
    <IntegrationsViskanSettingsModal :id="VISKAN_SETTINGS" />
  </div>
</template>

<script lang="ts">
import type { Company, CompanyData } from '@/types/Models/Company'
import type { IntegrationsModal } from '@/types/Models/Integration'
import type { UsersModal as UsersModalData } from '@/types/Models/User'
import { notify } from '@kyvg/vue3-notification'
import { BButton, BFormGroup, BSpinner } from 'bootstrap-vue-next'
import { Form as VeeForm } from 'vee-validate'
import { computed, defineComponent, ref, type ComputedRef, type Ref, onBeforeMount, onMounted } from 'vue'
import {
  INTEGRATION_DETAILS,
  INTEGRATION_ORDERS,
  INTEGRATIONS,
  MAGENTO_KEYS,
  ONGOING_WEBHOOK_SETTINGS,
  SENDER_ADDRESS_ACTION,
  SENDER_ADDRESSES,
  SHOPIFY_WEBHOOK_SETTINGS,
  USERS,
  USERS_CREATE_EDIT,
  VISKAN_SETTINGS,
  WOO_COMMERCE_KEYS,
} from '@/const/ModalTypes'
import { openModal, registerModals } from '@/services/Modal'
import { useCustomersStore } from '@/store/customers'
import { useCustomersIntegrationsStore } from '@/store/customers/integrations'
import { useCustomersSenderAddressesStore } from '@/store/customers/senderAddresses'
import VInput from '@/views/Components/Elements/Form/VInput.vue'
import UsersModal from '@/views/Modals/Customer/UsersListModal.vue'
import UserCreateEditModal from '@/views/Modals/Customer/UserCreateEditModal.vue'
import SenderAddressesModal from '@/views/Modals/SenderAddress/SenderAddressesModal.vue'
import IntegrationsActionModal from '@/views/Modals/Integrations/IntegrationsActionModal.vue'
import IntegrationsOrdersModal from '@/views/Modals/Integrations/IntegrationsOrdersModal.vue'
import IntegrationsDetailsModal from '@/views/Modals/Integrations/IntegrationsDetailsModal.vue'
import SenderAddressActionModal from '@/views/Modals/SenderAddress/SenderAddressActionModal.vue'
import IntegrationsMagentoKeysModal from '@/views/Modals/Integrations/IntegrationsMagentoKeysModal.vue'
import IntegrationsViskanSettingsModal from '@/views/Modals/Integrations/IntegrationsViskanSettingsModal.vue'
import IntegrationsOngoingSettingsModal from '@/views/Modals/Integrations/IntegrationsOngoingSettingsModal.vue'
import IntegrationsShopifySettingsModal from '@/views/Modals/Integrations/IntegrationsShopifySettingsModal.vue'
import IntegrationsWoocommerceKeysModal from '@/views/Modals/Integrations/IntegrationsWoocommerceKeysModal.vue'

export default defineComponent({
  name: 'Profile',
  components: {
    BSpinner,
    BButton,
    BFormGroup,
    UsersModal,
    IntegrationsMagentoKeysModal,
    IntegrationsWoocommerceKeysModal,
    IntegrationsOngoingSettingsModal,
    UserCreateEditModal,
    IntegrationsDetailsModal,
    IntegrationsActionModal,
    SenderAddressActionModal,
    SenderAddressesModal,
    IntegrationsShopifySettingsModal,
    IntegrationsViskanSettingsModal,
    IntegrationsOrdersModal,
    VInput,
    VeeForm,
  },
  setup() {
    const customersStore = useCustomersStore()
    const customersSenderAddressesStore = useCustomersSenderAddressesStore()
    const customersIntegrationsStore = useCustomersIntegrationsStore()

    const companyData: Ref<CompanyData> = ref({
      name: '',
      email: '',
      phoneNumber: '',
      contactName: '',
      contactLastName: '',
      externalId: '',
    })

    const company: ComputedRef<Company | null> = computed(() => customersStore.customer)

    const openSenderAddressesModal = () => {
      const currentCompany = company.value
      if (!currentCompany) return
      customersSenderAddressesStore
        .fetch(currentCompany.id)
        .then(() => {
          openModal(SENDER_ADDRESSES, currentCompany)
        })
        .catch(() => undefined)
    }

    const openIntegrationsModal = () => {
      const currentCompany = company.value
      if (!currentCompany) return
      customersIntegrationsStore
        .fetch(currentCompany.id)
        .then(() => {
          openModal(INTEGRATIONS, { id: currentCompany.id, company: currentCompany } as IntegrationsModal)
        })
        .catch(() => undefined)
    }

    const openUsersModal = () => {
      const currentCompany = company.value
      if (!currentCompany) return
      openModal(USERS, { companyId: currentCompany.id } as UsersModalData)
    }

    const submit = (val: CompanyData | any) => {
      if (!company.value) return
      customersStore
        .update({
          id: company.value.id,
          data: val,
        })
        .then(() => {
          notify({ group: 'success', text: 'Company info updated successfully.' })
        })
        .catch(() => {
          notify({ group: 'error', text: 'Failed to update company info.' })
        })
    }

    const loadCompany = () => {
      customersStore.me().then(() => {
        if (company.value) {
          companyData.value = { ...company.value }
        }
      })
    }

    onBeforeMount(loadCompany)
    onMounted(() => {
      registerModals(SENDER_ADDRESSES)
      registerModals(SENDER_ADDRESS_ACTION)

      registerModals(USERS)
      registerModals(USERS_CREATE_EDIT)

      registerModals(SHOPIFY_WEBHOOK_SETTINGS)
      registerModals(ONGOING_WEBHOOK_SETTINGS)
      registerModals(VISKAN_SETTINGS)
      registerModals(INTEGRATION_DETAILS)
      registerModals(INTEGRATION_ORDERS)
      registerModals(WOO_COMMERCE_KEYS)
      registerModals(MAGENTO_KEYS)
      registerModals(INTEGRATIONS)
    })

    return {
      company,
      companyData,
      submit,
      openUsersModal,
      openIntegrationsModal,
      openSenderAddressesModal,

      ONGOING_WEBHOOK_SETTINGS,
      SHOPIFY_WEBHOOK_SETTINGS,
      VISKAN_SETTINGS,
      SENDER_ADDRESS_ACTION,
      INTEGRATION_DETAILS,
      INTEGRATION_ORDERS,
      USERS_CREATE_EDIT,
      WOO_COMMERCE_KEYS,
      SENDER_ADDRESSES,
      MAGENTO_KEYS,
      INTEGRATIONS,
      USERS,
    }
  },
})
</script>
